import { TimePointParts } from '../calendar/time-point.js';
import { AssetVariant, ChangeOrigin, SessionChangePart, Tick } from '../domain/enums.js';
import { IMPORT_PROGRESS_PHASES } from '../domain/rows-enums.js';
import { IsoTimestamp, ReleaseId, Uuid } from '../dto/common.js';
import { ImportRunStatus } from '../dto/import.js';
import { InboxCategoryKey } from '../inbox/categories.js';
import { z } from '../zod.js';
import { APP_EVENT_ROWS } from './rows-app-events.js';
/**
 * Live updates on the app's SSE stream (`events.stream`), frozen by 1.0b deliverable 14. Every
 * event is `{type, v: 1, data}` (P0-3's framing, decision D11); the owner and gate of each type
 * are the rows of `APP_EVENT_ROWS`. A frozen event changes only additively.
 */
/** Moved to `src/domain/enums.ts` (1.0b deliverable 1); kept here so old imports resolve. */
export { APP_EVENT_ROWS, ChangeOrigin };
export const SysHeartbeatEvent = z.strictObject({
    type: z.literal('sys.heartbeat'),
    v: z.literal(1),
    data: z.strictObject({ at: IsoTimestamp }),
});
export const SysReleaseEvent = z.strictObject({
    type: z.literal('sys.release'),
    v: z.literal(1),
    data: z.strictObject({ release: ReleaseId }),
});
export const DocChangedEvent = z.strictObject({
    type: z.literal('doc.changed'),
    v: z.literal(1),
    data: z.strictObject({
        docId: Uuid,
        bodyVersion: z.number().int().min(1),
        metaVersion: z.number().int().min(1),
        origin: ChangeOrigin,
        clientSeq: z.number().int().nonnegative().optional(),
    }),
});
/** The lifecycle states `doc.lifecycle` reports. */
export const DOC_LIFECYCLE_STATES = ['created', 'trashed', 'restored', 'merged'];
export const DocLifecycleState = z.enum(DOC_LIFECYCLE_STATES);
/** A document was created, trashed, restored or merged; `mergedIntoId` is set only for a merge. */
export const DocLifecycleEvent = z.strictObject({
    type: z.literal('doc.lifecycle'),
    v: z.literal(1),
    data: z
        .strictObject({ docId: Uuid, state: DocLifecycleState, mergedIntoId: Uuid.nullable() })
        .refine((d) => (d.state === 'merged') === (d.mergedIntoId !== null), {
        message: 'mergedIntoId is set exactly for a merge',
        path: ['mergedIntoId'],
    }),
});
/** Links of these documents changed. */
export const LinksChangedEvent = z.strictObject({
    type: z.literal('links.changed'),
    v: z.literal(1),
    data: z.strictObject({ docIds: z.array(Uuid) }),
});
/** These types (or their fields) changed. */
export const TypesChangedEvent = z.strictObject({
    type: z.literal('types.changed'),
    v: z.literal(1),
    data: z.strictObject({ typeIds: z.array(Uuid) }),
});
/**
 * The variants job finished: the variant names it wrote, possibly none (an animated image gets no
 * variant and keeps its original; amended for Waves 2 and 3).
 */
export const AssetReadyEvent = z.strictObject({
    type: z.literal('asset.ready'),
    v: z.literal(1),
    data: z.strictObject({ assetId: Uuid, variants: z.array(AssetVariant) }),
});
/** The finished states of an export job, as `export.ready` reports them. */
export const EXPORT_READY_STATUSES = ['succeeded', 'failed'];
export const ExportReadyStatus = z.enum(EXPORT_READY_STATUSES);
/** An on-demand export finished. */
export const ExportReadyEvent = z.strictObject({
    type: z.literal('export.ready'),
    v: z.literal(1),
    data: z.strictObject({ exportId: Uuid, status: ExportReadyStatus }),
});
/** The undismissed count of each Inbox category, for the sidebar badge. */
export const InboxCountsEvent = z.strictObject({
    type: z.literal('inbox.counts'),
    v: z.literal(1),
    data: z.strictObject({
        counts: z.array(z.strictObject({ category: InboxCategoryKey, count: z.number().int().nonnegative() })),
    }),
});
/**
 * Placements, constraints or spans of these documents changed, at this calendar version. An empty
 * `docIds` means every dated document: a calendar commit moves every date and publishes it so
 * (CS r1d, DECISIONS 98 item 12). Read it with `chronoChangeTouches`, never with a test of one's own.
 */
export const ChronoChangedEvent = z.strictObject({
    type: z.literal('chrono.changed'),
    v: z.literal(1),
    data: z.strictObject({
        docIds: z.array(Uuid),
        calendarVersion: z.number().int().nonnegative(),
    }),
});
/** Whether a `chrono.changed` concerns this document: it is listed, or `docIds` is empty (all). */
export function chronoChangeTouches(event, docId) {
    return event.data.docIds.length === 0 || event.data.docIds.includes(docId);
}
/** The campaign clock moved: its present tick and parts, null while NOW is unset. */
export const ClockChangedEvent = z.strictObject({
    type: z.literal('clock.changed'),
    v: z.literal(1),
    data: z.strictObject({ presentTick: Tick.nullable(), presentParts: TimePointParts.nullable() }),
});
/** Secret states or play and discovery roll-ups changed. */
export const LedgerChangedEvent = z.strictObject({
    type: z.literal('ledger.changed'),
    v: z.literal(1),
    data: z.strictObject({ secretIds: z.array(Uuid), docIds: z.array(Uuid) }),
});
/**
 * An import run moved on (CS r4a; 2.B-1): `done` of `total` pages planned, applied or undone, and
 * the run's status. The worker publishes it in its own short transaction, at most every 250 ms
 * and at each status change.
 */
export const ImportProgressEvent = z.strictObject({
    type: z.literal('import.progress'),
    v: z.literal(1),
    data: z
        .strictObject({
        runId: Uuid,
        phase: z.enum(IMPORT_PROGRESS_PHASES),
        done: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        status: ImportRunStatus,
    })
        .refine((d) => d.done <= d.total, { message: 'done is at most total', path: ['done'] }),
});
/**
 * A session changed (CS r5a deliverable 146; 2.C from stage s1): which parts, so Tonight, Run mode
 * and After refetch only those (the run sheet, attendance, captures, sources or the session's own
 * columns).
 */
export const SessionChangedEvent = z.strictObject({
    type: z.literal('session.changed'),
    v: z.literal(1),
    data: z.strictObject({
        sessionId: Uuid,
        parts: z.array(SessionChangePart).min(1).max(5),
    }),
});
export const AppEventV1 = z.discriminatedUnion('type', [
    SysHeartbeatEvent,
    SysReleaseEvent,
    DocChangedEvent,
    DocLifecycleEvent,
    LinksChangedEvent,
    TypesChangedEvent,
    AssetReadyEvent,
    ExportReadyEvent,
    InboxCountsEvent,
    ChronoChangedEvent,
    ClockChangedEvent,
    LedgerChangedEvent,
    ImportProgressEvent,
    SessionChangedEvent,
]);
export const APP_EVENT_TYPES = APP_EVENT_ROWS.map((row) => row.type);
//# sourceMappingURL=app-events.js.map